import { api } from "../../api/apiSlice";

// AMC & Warranty - Customer Equipment register (Batch135, api/v1/amc).
//
// The register is the single source of truth for what a customer has. It is written
// from three user-chosen sources on one screen - an existing Stock Out, a Quotation
// Prototype, or the plain product list - and never derived automatically from any of
// them. Only products flagged HasWarranty on the product master can enter it, which is
// what keeps cable and screws out without any consumable classification.
//
// One permission (Amc.ManageEquipment) covers view and manage together - there is
// deliberately no separate .View code, per direct instruction.
export const amcApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerEquipment: builder.query({
      query: (params) => ({ url: "/amc/equipment", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((e) => ({ type: "AmcEquipment", id: e.recordGuid })),
              { type: "AmcEquipment", id: "LIST" },
            ]
          : [{ type: "AmcEquipment", id: "LIST" }],
    }),

    getEquipmentItem: builder.query({
      query: (recordGuid) => ({ url: `/amc/equipment/${recordGuid}` }),
      providesTags: (r, e, recordGuid) => [{ type: "AmcEquipment", id: recordGuid }],
    }),

    addCustomerEquipment: builder.mutation({
      query: (body) => ({ url: "/amc/equipment", method: "POST", data: body }),
      invalidatesTags: [{ type: "AmcEquipment", id: "LIST" }],
    }),

    updateCustomerEquipment: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/amc/equipment/${recordGuid}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (r, e, { recordGuid }) => [
        { type: "AmcEquipment", id: recordGuid },
        { type: "AmcEquipment", id: "LIST" },
      ],
    }),

    deleteCustomerEquipment: builder.mutation({
      query: ({ recordGuid, reason }) => ({
        url: `/amc/equipment/${recordGuid}/delete`,
        method: "POST",
        data: { reason },
      }),
      invalidatesTags: [{ type: "AmcEquipment", id: "LIST" }],
    }),

    // Prefill source 1. AvailableQty is already net of returns and alreadyAdded flags
    // lines pulled before, so the picker can disable them rather than letting the user
    // hit the server's duplicate index.
    getStockOutCandidates: builder.query({
      query: (params) => ({ url: "/amc/equipment/stock-out-candidates", params }),
      providesTags: [{ type: "AmcEquipment", id: "STOCKOUT_CANDIDATES" }],
    }),

    // Prefill source 2. Every line that cannot become equipment comes back with an
    // ineligibleReason, which the picker shows verbatim instead of dropping it silently.
    getPrototypeCandidates: builder.query({
      query: (prototypeRecordGuid) => ({
        url: `/amc/equipment/prototype-candidates/${prototypeRecordGuid}`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCustomerEquipmentQuery,
  useGetEquipmentItemQuery,
  useAddCustomerEquipmentMutation,
  useUpdateCustomerEquipmentMutation,
  useDeleteCustomerEquipmentMutation,
  useGetStockOutCandidatesQuery,
  useLazyGetStockOutCandidatesQuery,
  useLazyGetPrototypeCandidatesQuery,
} = amcApi;

// ---- AMC contracts + visit schedule (Batch136) ----
// Kept in the same slice as equipment because the New AMC screen reads both together:
// a contract, its covered equipment and its visit schedule are one page, not three.
export const amcContractsApi = amcApi.injectEndpoints({
  endpoints: (builder) => ({
    getAmcContracts: builder.query({
      query: (params) => ({ url: "/amc/contracts", params }),
      providesTags: [{ type: "AmcContract", id: "LIST" }],
    }),
    getAmcContract: builder.query({
      query: (recordGuid) => ({ url: `/amc/contracts/${recordGuid}` }),
      providesTags: (r, e, g) => [{ type: "AmcContract", id: g }],
    }),
    createAmcContract: builder.mutation({
      query: (body) => ({ url: "/amc/contracts", method: "POST", data: body }),
      invalidatesTags: [{ type: "AmcContract", id: "LIST" }, { type: "AmcSchedule", id: "LIST" }],
    }),
    updateAmcContract: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/amc/contracts/${recordGuid}`, method: "PUT", data: body }),
      invalidatesTags: (r, e, { recordGuid }) => [
        { type: "AmcContract", id: recordGuid },
        { type: "AmcContract", id: "LIST" },
        { type: "AmcSchedule", id: "LIST" },
      ],
    }),
    // Cancel or reactivate (Batch148). A cancelled contract stops appearing in
    // the service-due and renewals-due lists (both filter Status='ACTIVE'
    // server-side) but stays in the contract list under a CANCELLED badge.
    // Distinct from delete below, which is refused outright once real service
    // visits exist - cancelling is what to do then.
    // USER-TRIGGERED renewal reminder (Batch149) - there is no scheduler
    // anywhere in this feature. Each channel is attempted and logged
    // independently server-side, and the full history comes back so the caller
    // can report what actually happened per channel.
    sendAmcRenewalReminder: builder.mutation({
      query: ({ recordGuid, channels, customMessage }) => ({
        url: `/amc/contracts/${recordGuid}/renewal-reminder`, method: "POST",
        data: { channels, customMessage },
      }),
      invalidatesTags: [{ type: "AmcContract", id: "LIST" }],
    }),
    setAmcContractStatus: builder.mutation({
      query: ({ recordGuid, status, reason }) => ({
        url: `/amc/contracts/${recordGuid}/status`, method: "POST", data: { status, reason },
      }),
      invalidatesTags: (r, e, { recordGuid }) => [
        { type: "AmcContract", id: recordGuid },
        { type: "AmcContract", id: "LIST" },
        { type: "AmcSchedule", id: "LIST" },
      ],
    }),
    deleteAmcContract: builder.mutation({
      query: ({ recordGuid, reason }) => ({ url: `/amc/contracts/${recordGuid}/delete`, method: "POST", data: { reason } }),
      invalidatesTags: [{ type: "AmcContract", id: "LIST" }, { type: "AmcSchedule", id: "LIST" }],
    }),

    getAmcContractEquipment: builder.query({
      query: (recordGuid) => ({ url: `/amc/contracts/${recordGuid}/equipment` }),
      providesTags: (r, e, g) => [{ type: "AmcContract", id: `${g}-EQUIP` }],
    }),
    // Diff-style: the full desired set. An empty list is valid - coverage is optional.
    setAmcContractEquipment: builder.mutation({
      query: ({ recordGuid, equipmentIds }) => ({
        url: `/amc/contracts/${recordGuid}/equipment`,
        method: "PUT",
        data: { equipmentIds },
      }),
      invalidatesTags: (r, e, { recordGuid }) => [
        { type: "AmcContract", id: `${recordGuid}-EQUIP` },
        { type: "AmcContract", id: "LIST" },
      ],
    }),

    getAmcSchedule: builder.query({
      query: (params) => ({ url: "/amc/schedule", params }),
      providesTags: [{ type: "AmcSchedule", id: "LIST" }],
    }),
    createVisitFromSchedule: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/amc/schedule/${recordGuid}/create-visit`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "AmcSchedule", id: "LIST" }, { type: "AmcContract", id: "LIST" }],
    }),
    skipScheduledVisit: builder.mutation({
      query: ({ recordGuid, skipReason }) => ({
        url: `/amc/schedule/${recordGuid}/skip`,
        method: "POST",
        data: { skipReason },
      }),
      invalidatesTags: [{ type: "AmcSchedule", id: "LIST" }],
    }),

    // ---- Dashboard lists (Batch140) ----
    // Both are already banded server-side from the admin's day settings, and both
    // deliberately reuse the existing LIST tags rather than introducing their own:
    // raising a visit or creating a contract must refresh the dashboard, and those
    // mutations already invalidate exactly these two tags.
    getAmcServiceDue: builder.query({
      query: (params) => ({ url: "/amc/service-due", params }),
      providesTags: [{ type: "AmcSchedule", id: "LIST" }],
    }),
    getAmcRenewalsDue: builder.query({
      query: (params) => ({ url: "/amc/renewals-due", params }),
      providesTags: [{ type: "AmcContract", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAmcContractsQuery,
  useGetAmcContractQuery,
  useCreateAmcContractMutation,
  useUpdateAmcContractMutation,
  useDeleteAmcContractMutation,
  useSetAmcContractStatusMutation,
  useSendAmcRenewalReminderMutation,
  useGetAmcContractEquipmentQuery,
  useSetAmcContractEquipmentMutation,
  useGetAmcScheduleQuery,
  useCreateVisitFromScheduleMutation,
  useSkipScheduledVisitMutation,
  useGetAmcServiceDueQuery,
  useGetAmcRenewalsDueQuery,
} = amcContractsApi;
